import moment from "moment";
import debug from "debug";
import { debounce } from "lodash";
import { action, observable, reaction, computed, toJS, autorun } from "mobx";
import {
  ConsultantStatus,
  InitialContact,
  CustomerType,
  ExpertiseFragment,
  LastContactType,
  LocationKlm,
  Workshift,
  ConsultantQueryBookingStatus,
  NumericRangeInput,
  LegalPersonTypeWithNoOption,
  GDPROptionType,
  UserSelectFragment
} from "@graphql";
import locale from "@bonliva-crm/locale";
import { Option } from "@components/Select";
import { BaseStore } from "@store/BaseStore";
import { SortOption } from "./Utils";
import { store, Store } from "@store";
import { socionomInitialValues } from "../consultants/ConsultantSearch/professions/socionom";
import { sskInitialValues } from "../consultants/ConsultantSearch/professions/ssk";
import { expOnlyInitialValue } from "../consultants/ConsultantSearch/professions/exp_only/utils";
import { teacherInitialValues } from "../consultants/ConsultantSearch/professions/teacher/utils";
import { aoeSectionInitialValues } from "../consultants/ConsultantSearch/professions/aoe_section/utils";
import { radiographerInitialValues } from "../consultants/ConsultantSearch/professions/radiographer/utils";
import { psykologInitialValues } from "../consultants/ConsultantSearch/professions/psykolog/utils";
import { professions } from "@bonliva-crm/common";
import {
  ApprovedAuthorizationOption,
  WorkedInNorwayOption,
  NorwayConsultantStatus,
  SelectType,
  SearchQualityCheckStatus
} from "@bonliva-crm/shared";

export const initialSearchParams = {
  availableForWorkInCountries: ["Sweden"],
  areasOfExpertiseProfessions: [] as string[],
  areasOfExpertiseExperience: "",
  callListIds: [] as string[],
  competences: [] as string[],
  competitors: [] as string[],
  customerType: [] as CustomerType[],
  customerNumbers: "",
  journalSystem: null,
  customerGroup: [] as string[],
  initialContact: [] as InitialContact[],
  locationPos: {
    lat: null as number,
    lng: null as number
  },
  fitness: "fit" as "fit" | "unfit" | "all",
  from: moment.utc().toDate(),
  gdprMarketing: [] as GDPROptionType[],
  gdprIntegrationPolicy: [] as GDPROptionType[],
  gdprJobMailingEmail: [] as GDPROptionType[],
  gdprJobMailingSms: [] as GDPROptionType[],
  legalBasis: "null",
  hprNumber: "null",
  workedInNorway: WorkedInNorwayOption.All,
  approvedAuthorization: ApprovedAuthorizationOption.All,
  norwayConsultantStatus: null as NorwayConsultantStatus,
  organizationType: [] as string[],
  hasDrivingLicence: false,
  knownSystems: [] as string[],
  customerIds: [] as LabelValue[],
  lastContact: [null, null] as [Date | null, Date | null],
  lastContactType: [] as LastContactType[],
  lastContactSelectType: SelectType.AND,
  myActivity: false as boolean,
  qualification: "all" as "all" | "showQualified" | "showExpired" | "showUnqualified",
  nationalQualification: "all" as
    | "all"
    | "showQualified"
    | "showExpired"
    | "showUnqualified",
  participatesInVaccination: "null" as "null" | "true" | "false",
  radius: 100,
  regions: [] as string[],
  responsiblePerson: [] as string[],
  roles: [] as string[],
  searchType: "basic" as "basic" | "extended",
  senior: false,
  specializations: [] as string[],
  status: [ConsultantStatus.Active],
  tags: [] as string[],
  team: "",
  text: "",
  sort: "name_asc" as SortOption,
  to: moment.utc().add(6, "month").toDate(),
  workshifts: [] as Workshift[],
  location: {
    zipOrCity: "",
    klm: "20"
  } as LocationKlm,
  isValid: "null" as "true" | "false" | "null",
  professionsForm: {
    professions: [] as string[],
    doctor: aoeSectionInitialValues,
    expOnly: expOnlyInitialValue,
    psykolog: psykologInitialValues,
    radiographer: radiographerInitialValues,
    socialWorkerForm: socionomInitialValues,
    sskForm: sskInitialValues,
    teacherForm: teacherInitialValues
  },
  bookingStatusOption: null,
  bookingStatus: null as ConsultantQueryBookingStatus | null,
  bookingStatusDate: null as Date | null,
  bookingStatusDateEnd: null as Date | null,

  wagesRange: null as NumericRangeInput,
  ageRange: null as NumericRangeInput,
  hasLegalPersonType: null as LegalPersonTypeWithNoOption,
  hasEmail: "null",
  hasPhone: "null",
  leadStatus: null,
  interested: "null",
  experienced: "null",
  experience: false,
  interest: false,
  consultants: [] as LabelValue[],
  qualityCheckName: null,
  qualityCheckStatuses: [] as SearchQualityCheckStatus[]
};

export type GeneralSearchData = typeof initialSearchParams;
export type SearchStoreOptions<T> = {
  defaultGroupBy?: string;
  /**
   * Search Store unique name for debugging purposes
   */
  name?: string;

  /**
   * Initial Search Params object (default search params)
   */
  initialSearchParams?: T;
};

export type SearchCallback<T> = (searchParams: Partial<T>) => void;

export class GeneralSearchStore extends BaseStore {
  @computed get searchParams() {
    return this._searchParams;
  }

  @computed get teams(): string[] {
    return this.rootStore.user.myProfile.Teams;
  }

  @computed get areasOfExp(): ExpertiseFragment[] {
    const { cache, user } = this.rootStore;
    const { myProfile } = user;

    if (!cache.areasOfExp) {
      return [];
    }

    return [
      ...cache.getAreasOfExpOnTeams(myProfile.Teams),
      ...user.myProfile.ExtraAreasOfExpertise
    ];
  }

  @computed get areasOfExpertiseWithExclusiveOptions(): Option[] {
    const exp: Option[] = this.areasOfExp
      .sortBy(
        (x) => !x.Exclusive,
        (x) => x.Name
      )
      .map((a) => ({
        label: a.Exclusive ? locale.Only(a.Name) : a.Name,
        value: a.Name,
        parent: a.Profession,
        desc: a.Aliases.join(", ")
      }));

    const profs: Option[] = this.areasOfExp
      .map((e) => e.Profession)
      .unique()
      .toLabelValue((x) => professions.getProfessionDisplayName(x));

    return exp.concat(profs);
  }

  @computed get areasOfExpertiseOptions(): Option[] {
    const exp: Option[] = this.areasOfExp
      .map((x) => ({
        label: x.Name,
        value: x.Name,
        parent: x.Profession,
        desc: x.Aliases.join(", ")
      }))
      .concat([
        {
          label: "Socionom (enbart)",
          value: "Socionom only",
          parent: "Socionom",
          desc: ""
        }
      ])
      .sortBy((x) => x.label);

    const profs: Option[] = this.areasOfExp
      .map((x) => x.Profession)
      .unique()
      .toLabelValue((x) => professions.getProfessionDisplayName(x));

    return exp.concat(profs);
  }

  @computed get professions(): string[] {
    return this.rootStore.cache.professions;
  }

  @computed get myProfessions(): string[] {
    if (!this.rootStore.cache.teams) {
      return [];
    }
    return this.rootStore.cache.getProfessionsOnTeams(this.teams);
  }

  @computed get regions(): Option[] {
    return this.rootStore.cache.regionsByCountries.sortBy(
      (x) => !x.value.startsWith("Sweden")
    );
  }

  @computed get regionsWithBonlivaCare(): Option[] {
    return this.rootStore.cache.regionsByCountriesWithBonlivaCare.sortBy(
      (x) => !x.value.startsWith("Sweden")
    );
  }

  @computed get sortOption() {
    return this._sortOption;
  }

  set sortOption(v: SortOption) {
    this._sortOption = v;
    this._searchCallback(this._searchParams);
  }

  @computed get alphabetFilter() {
    return this._alphabetFilter;
  }

  set alphabetFilter(v: string[] | null) {
    this._alphabetFilter = v;
    this._searchCallback(this._searchParams);
  }

  @computed get allSpecializations(): string[] {
    const { cache } = this.rootStore;
    const { areasOfExpertiseProfessions } = this._searchParams;

    // if nothing selected, then return all possible specializations
    if (
      !Array.isArray(areasOfExpertiseProfessions) ||
      areasOfExpertiseProfessions.length === 0
    ) {
      return cache.getSpecializationsOfExpertises(this.areasOfExp.map((a) => a.Name));
    }

    const areaOfExp = cache.getAreasOfExpOnAreasOfExpAndProfessions(
      areasOfExpertiseProfessions
    );

    return cache.getSpecializationsOfExpertises(areaOfExp.map((a) => a.Name));
  }

  @computed get tags(): string[] {
    return this.rootStore.cache.teams
      .map((t) => toJS(t.Tags))
      .selectMany((x) => x)
      .unique();
  }

  @computed get roles() {
    return emptyIfFalsy(this.rootStore.cache.roles);
  }

  @computed get knownSystems(): string[] {
    const { user } = this.rootStore;
    const { myProfile } = user;

    if (!myProfile) {
      console.error("my profile is missing");
      return [];
    }

    if (!myProfile.Teams || myProfile.Teams.length === 0) {
      return [];
    }

    return this.rootStore.cache.teams
      .filter((team) => myProfile.Teams.includes(team.Name))
      .map((team) => team.KnownSystems.slice())
      .selectMany((x) => x)
      .unique();
  }

  get allCompetitors() {
    return emptyIfFalsy(this.rootStore.cache.competitors);
  }

  get countries(): string[] {
    return emptyIfFalsy(this.rootStore.cache.countries);
  }

  get journalSystems() {
    return store().cache.systems;
  }

  options: SearchStoreOptions<GeneralSearchData>;
  log: debug.IDebugger;

  @observable groupBy: string;
  @observable customerTypes = Object.values(CustomerType).map((k) => CustomerType[k]);
  @observable count: number = null;
  @observable isToggled = false; // is form expanded or collapsed
  @observable isMoreDisabledInSticky = false; // super hack for consultant search, where Mer/Mindre buttons don't work in a pull down sticky filter

  @observable protected _searchParams: GeneralSearchData = initialSearchParams;
  private _searchCallbackDebounced: SearchCallback<GeneralSearchData> & { clear(): void };
  private _searchCallback: SearchCallback<GeneralSearchData>;
  @observable private _sortOption: SortOption = "name_asc";
  @observable private _alphabetFilter: string[] | null = null;

  constructor(public rootStore: Store) {
    super();

    this._searchParams = this.getInitialSearchParams();
    this.options = {
      defaultGroupBy: "none",
      name: "GeneralSearchStore"
    };
    this.log = debug(`app:store:${this.options.name}`);

    // start monitoring location hash
    reaction(() => this.rootStore.routing.location.hash, this.getGroupBy, {
      fireImmediately: true
    });

    this.init()
      .then(() => this.setReady())
      .catch((e) => this.setError(e));

    let previous: string = JSON.stringify(this.searchParams);

    autorun(() => {
      const current = toJS(this._searchParams);
      const currentJson = JSON.stringify(current);
      if (currentJson !== previous) {
        this._searchCallback(current);
        previous = currentJson;
      }
    });
  }

  /**
   * Store init func
   */
  async init(): Promise<void> {
    return Promise.resolve();
  }

  @action.bound setSearchParams(v: Partial<GeneralSearchData>) {
    this._searchParams = {
      ...this._searchParams,
      ...v
    };
  }

  @action.bound registerSearchCallback(cb: SearchCallback<GeneralSearchData>) {
    this._searchCallback = debounce(cb, 400, { leading: true });
    cb(this.searchParams);
  }

  @action.bound getGroupBy() {
    const { hash } = this.rootStore.routing.location;

    // must start with #
    if (!hash || hash.charAt(0) !== "#") {
      this.groupBy = this.options.defaultGroupBy;
    } else {
      const groupBy = hash.split("#")[1];
      this.groupBy = groupBy ? groupBy : this.options.defaultGroupBy;
    }
  }

  @action.bound setGroupBy(v: string) {
    const url =
      v === this.options.defaultGroupBy
        ? this.rootStore.routing.location.pathname
        : `${this.rootStore.routing.location.pathname}#${v}`;

    this.rootStore.routing.history.push(url);
    this.groupBy = v;

    this._searchCallback(this.searchParams);
  }

  @action toggleForm = () => {
    this.isToggled = !this.isToggled;
  };

  @action clearForm = () => {
    this._searchParams = this.getInitialSearchParams();
  };

  private getInitialSearchParams = () => ({
    ...initialSearchParams,
    responsiblePerson: initialSearchParams.responsiblePerson.any()
      ? initialSearchParams.responsiblePerson
      : [this.rootStore.user.myProfile._id]
  });
}

export function usersToOptions(users: UserSelectFragment[]): Option[] {
  const enabledUsers: Option[] = [];
  const disabledUsers: Option[] = [];

  for (const user of users) {
    if (user.Enabled) {
      enabledUsers.push({
        label: user.Name,
        value: user._id
      });
    } else {
      disabledUsers.push({
        label: user.Name,
        value: user._id,
        style: { opacity: 0.6 }
      });
    }
  }

  let result = [
    ...enabledUsers,
    { label: "----", value: "", disabled: true, divider: true },
    ...disabledUsers
  ];

  // Remove the divider if there are no disabled users
  if (result.length === 1) {
    result = [];
  }

  return result;
}

export const emptyIfFalsy = <T>(array: T[]): T[] => (!array ? [] : array);
